import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import api from "../lib/api";
import ClayCard from "../components/ClayCard";
import ChristmasBackdrop from "../components/ChristmasBackdrop";
import HeroLogo from "../components/HeroLogo";
import StylizedLogo from "../components/StylizedLogo";
import Countdown from "../components/Countdown";
import { GraduationCap, Trophy, Users, BookOpen, Medal, Sparkles, FileText, X, Award, Download } from "lucide-react";
import { getButtonIcon } from "../lib/buttonIcons";
import DiplomaFetchModal from "../components/DiplomaFetchModal";
import SideButtons from "../components/SideButtons";
import PromoCard from "../components/PromoCard";
import MainHero from "../components/MainHero";
import { CARD2_DEFAULTS } from "../components/PromoCardEditor";

const EXTRA_COLORS = ["coral", "teal", "gold", "pine", "midnight"];

export default function Home() {
  const navigate = useNavigate();
  const { t, lang, settings, texts } = useApp();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const siteText = (key) => {
    const e = texts && texts[key];
    if (!e) return "";
    return (e[lang] ?? e.pt ?? e.en ?? "").trim();
  };
  const editionBadge = siteText("edition_badge");
  const taglineText = siteText("tagline");
  const appNameText = siteText("app_name");
  const [customButtons, setCustomButtons] = useState([]);
  const [fixedButtons, setFixedButtons] = useState([]);
  const [textModal, setTextModal] = useState(null);
  const [diplomaMode, setDiplomaMode] = useState(null);
  const [imgRatio, setImgRatio] = useState(null);
  const [headerH, setHeaderH] = useState(96);
  const [promoExtraPx, setPromoExtraPx] = useState(0);
  const promoAnchor = (settings.promo_card && settings.promo_card.anchor) || "below_alunos";

  useEffect(() => {
    api.get("/home-buttons").then((r) => setCustomButtons(r.data || [])).catch(() => {});
    api.get("/home-fixed-buttons").then((r) => setFixedButtons(r.data || [])).catch(() => {});
  }, []);

  const onCustomClick = (b) => {
    if (b.content_type === "pdf" && b.pdf_path) {
      window.open(`${backendUrl}/api/files/${b.pdf_path}`, "_blank", "noopener");
    } else {
      setTextModal({
        title: lang !== "en" ? b.label_pt : b.label_en,
        content: lang !== "en" ? b.content_pt : b.content_en,
      });
    }
  };

  const bgPath = lang === "en" ? (settings.background_image_path_en || settings.background_image_path) : (settings.background_image_path || settings.background_image_path_en);
  const bgUrl = bgPath ? `${backendUrl}/api/files/${bgPath}` : null;

  const heightMode = settings.bg_height_mode || "screen";
  const heightValue = settings.bg_height_value || "";
  const belowHeader = settings.bg_below_header !== false;

  useEffect(() => {
    const measure = () => {
      const h = document.querySelector('[data-testid="site-header-wrap"]');
      setHeaderH(h ? h.offsetHeight : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [settings]);

  useEffect(() => {
    setImgRatio(null);
    if (heightMode === "auto" && bgUrl) {
      const im = new Image();
      im.onload = () => { if (im.naturalWidth) setImgRatio(im.naturalHeight / im.naturalWidth); };
      im.src = bgUrl;
    }
  }, [heightMode, bgUrl]);

  useEffect(() => {
    const pc = settings.promo_card;
    if (!pc || pc.enabled === false || promoAnchor !== "below_alunos") { setPromoExtraPx(0); return; }
    const topPct = settings.side_alunos_top_pct ?? 5;
    const update = () => {
      const col = document.querySelector('[data-testid="side-col-alunos"]');
      if (!col || col.offsetParent === null) { setPromoExtraPx(0); return; }
      const H = col.offsetHeight;
      const frac = Math.max(0.2, 1 - topPct / 100);
      const needed = (H + 56) / frac;
      setPromoExtraPx(Math.max(0, Math.round(needed)));
    };
    update();
    window.addEventListener("resize", update);
    const id = setInterval(update, 700);
    return () => { window.removeEventListener("resize", update); clearInterval(id); };
  }, [settings, promoAnchor]);

  let bgMinHeight;
  if (bgUrl) {
    if (heightMode === "screen") bgMinHeight = `calc(100vh - ${headerH}px)`;
    else if (heightMode === "manual" && heightValue) bgMinHeight = heightValue;
    else if (heightMode === "auto" && imgRatio) bgMinHeight = `${(imgRatio * 100).toFixed(2)}vw`;
  }
  if (promoExtraPx > 0) {
    bgMinHeight = bgMinHeight
      ? `max(${bgMinHeight}, ${promoExtraPx}px)`
      : `${promoExtraPx}px`;
  }

  const logoPath = lang === "en" ? settings.logo_image_path_en : settings.logo_image_path_pt;
  const logoUrl = logoPath ? `${backendUrl}/api/files/${logoPath}` : null;

  const showLogo = settings.show_logo_image !== false && logoUrl;
  const showText = settings.show_logo_text !== false;
  const position = settings.countdown_position || "below_tagline";

  const FIXED_META = {
    train: { icon: GraduationCap, testId: "student-train-cta", sub_pt: "Pratica quizzes para te divertires", sub_en: "Practice quizzes for fun", run: () => navigate("/train") },
    compete: { icon: Trophy, testId: "student-compete-cta", sub_pt: "Entra com a tua credencial", sub_en: "Log in with your credential", run: () => navigate("/compete") },
    enroll: { icon: Users, testId: "teacher-enroll-cta", sub_pt: "Inscreve a turma no concurso", sub_en: "Enroll your class in the contest", run: () => navigate("/enroll") },
    rules: { icon: BookOpen, testId: "teacher-rules-cta", sub_pt: "Lê as regras do concurso", sub_en: "Read the contest rules", run: () => navigate("/rules") },
    results: { icon: Medal, testId: "view-results-cta", sub_pt: "Vê o ranking dos melhores", sub_en: "See the top rankings", run: () => navigate("/results") },
    student_diploma: { icon: Award, testId: "student-diploma-cta", sub_pt: "Entra com a tua credencial", sub_en: "Log in with your credential", run: () => setDiplomaMode("student") },
    teacher_diploma: { icon: Download, testId: "teacher-diploma-cta", sub_pt: "Indica o teu email", sub_en: "Enter your email", run: () => setDiplomaMode("teacher") },
  };

  const visibleFixed = fixedButtons.filter((b) => b.active !== false && b.visible !== false && FIXED_META[b.key]);

  const cd = <Countdown />;

  return (
    <>
      <ChristmasBackdrop
        bgUrl={bgUrl}
        gradient={settings.background_gradient}
        customGradient={settings.background_gradient_custom}
        overlayOpacity={settings.background_overlay_opacity}
        overlayColor={settings.bg_overlay_color}
        fit={settings.bg_fit}
        position={settings.bg_position}
        blur={settings.bg_blur}
        zoom={settings.bg_zoom}
        kenBurns={settings.bg_ken_burns}
        parallax={settings.bg_parallax}
        glass={settings.bg_glass}
        blendMode={settings.bg_blend_mode}
        fixed={settings.bg_fixed}
        offsetY={settings.bg_offset_y}
        effect={settings.background_effect}
        effectOpacity={settings.background_effect_opacity}
        effectBlend={settings.background_effect_blend}
        mobileHero={true}
        topOffset={belowHeader ? 0 : headerH}
        overlays={(settings.bg_overlays || []).map((o) => {
          const path = lang === "en" ? (o.image_path_en || o.image_path) : (o.image_path || o.image_path_en);
          return {
            enabled: o.enabled,
            url: path ? `${backendUrl}/api/files/${path}` : "",
            fit: o.fit, position: o.position, blend: o.blend, blur: o.blur, opacity: o.opacity,
            zoom: o.zoom, kenBurns: o.ken_burns, parallax: o.parallax, animMode: o.anim_mode,
            delay: o.delay, fade: o.fade, visible: o.visible, interval: o.interval,
          };
        })}
      />
    <div className="relative px-4 sm:px-6 md:px-10 pt-8 md:pt-10 pb-10 max-w-7xl mx-auto overflow-x-clip max-lg:!min-h-[73vh]" data-testid="home-page" style={bgMinHeight ? { minHeight: bgMinHeight } : undefined}>

      <div className="text-center mb-10 md:mb-14 relative">
        {settings.main_hero?.enabled && (
          <div className="mb-8" data-testid="main-hero-wrap">
            <MainHero config={settings.main_hero} lang={lang} backendUrl={backendUrl} countdown={settings.countdown_enabled ? cd : null} countdownPos={position} />
          </div>
        )}
        {position === "below_badge" ? (
          <div className="flex flex-col items-center gap-4">
            {editionBadge && (
              <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border font-${settings.hero_badge_font || "nunito"} font-bold text-sm shadow-sm`}
                style={{ backgroundColor: settings.hero_badge_bg_color || "#FFD93D", borderColor: settings.hero_badge_bg_color || "#FFD93D", color: settings.hero_badge_text_color || "#0B1D3A" }}>
                <Sparkles size={16} /> {editionBadge}
              </span>
            )}
            {cd}
          </div>
        ) : (
          editionBadge && (
            <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border font-${settings.hero_badge_font || "nunito"} font-bold text-sm shadow-sm`}
              style={{ backgroundColor: settings.hero_badge_bg_color || "#FFD93D", borderColor: settings.hero_badge_bg_color || "#FFD93D", color: settings.hero_badge_text_color || "#0B1D3A" }}>
              <Sparkles size={16} /> {editionBadge}
            </span>
          )
        )}

        {settings.hero_logo?.enabled && (
          <div className="mt-5 mb-2">
            <HeroLogo config={settings.hero_logo} lang={lang} backendUrl={backendUrl} />
          </div>
        )}

        {showLogo && (
          <div className="mt-5 mb-2 flex justify-center">
            <img src={logoUrl} alt="" className="h-20 md:h-28" />
          </div>
        )}
        {showText && appNameText && (
          <div className={`${showLogo ? "mt-2" : "mt-5"} mb-2 flex justify-center`}>
            <StylizedLogo lang={lang} text={appNameText} size="lg" />
          </div>
        )}

        {position === "below_logo" && cd}

        {taglineText && (
          <p className={`font-${settings.hero_tagline_font || "nunito"} text-base md:text-lg mt-2 max-w-2xl mx-auto`}
            style={{ color: settings.hero_tagline_color || "#0B1D3A" }}>
            {taglineText}
          </p>
        )}

        {position === "below_tagline" && cd}
        {position === "center" && <div className="flex justify-center mt-4" data-testid="countdown-center">{cd}</div>}
      </div>

      <div className="flex flex-wrap justify-center gap-5 md:gap-7">
        {visibleFixed.map((b) => {
          const m = FIXED_META[b.key];
          return (
            <div key={b.key} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.2rem)]">
              <ClayCard color={b.color} format={b.format} effect={b.effect} icon={getButtonIcon(b.icon, m.icon)} title={lang !== "en" ? b.label_pt : b.label_en} subtitle={lang !== "en" ? (b.subtitle_pt ?? m.sub_pt) : (b.subtitle_en ?? m.sub_en)} onClick={m.run} testId={m.testId} />
            </div>
          );
        })}
        {customButtons.map((b, i) => (
          <div key={b.id} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.2rem)]">
            <ClayCard
              color={b.color || EXTRA_COLORS[i % EXTRA_COLORS.length]}
              format={b.format}
              effect={b.effect}
              icon={getButtonIcon(b.icon, FileText)}
              title={lang !== "en" ? b.label_pt : b.label_en}
              subtitle={(lang !== "en" ? b.subtitle_pt : b.subtitle_en) || (b.content_type === "pdf" ? (lang !== "en" ? "Abrir documento PDF" : "Open PDF document") : (lang !== "en" ? "Ver informação" : "View information"))}
              onClick={() => onCustomClick(b)}
              testId={`home-custom-btn-${b.id}`}
            />
          </div>
        ))}
      </div>


      {position === "below_buttons" && <div className="mt-10 text-center">{cd}</div>}
      {position === "below_lang" && (
        <div className="absolute top-20 right-6 md:right-10 z-30">{cd}</div>
      )}
      {position === "below_logo_corner" && (
        <div className="absolute top-20 left-6 md:left-10 z-30">{cd}</div>
      )}

      {textModal && (
        <div className="fixed left-0 right-0 bottom-0 z-50 flex items-start justify-center bg-[#0B1D3A]/60 backdrop-blur-sm px-4 py-5 overflow-y-auto" style={{ top: `${headerH}px` }} data-testid="home-text-modal" onClick={() => setTextModal(null)}>
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full overflow-y-auto shadow-2xl relative" style={{ maxHeight: `calc(100vh - ${headerH}px - 2.5rem)` }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setTextModal(null)} data-testid="home-text-modal-close" className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0B1D3A]/5 hover:bg-[#0B1D3A]/10 text-[#0B1D3A]"><X size={18} /></button>
            <div className="flex items-center gap-3 mb-4 pr-10">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FF6B6B] text-white"><FileText size={20} /></span>
              <h2 className="font-fredoka text-2xl font-bold text-[#0B1D3A]">{textModal.title}</h2>
            </div>
            <p className="font-nunito text-[#0B1D3A]/80 whitespace-pre-wrap leading-relaxed">{textModal.content}</p>
          </div>
        </div>
      )}
      {diplomaMode && (
        <DiplomaFetchModal mode={diplomaMode} lang={lang} onClose={() => setDiplomaMode(null)} />
      )}
      {settings.promo_card_2?.enabled && (
        <div className="hidden lg:block">
          {position === "above_bottom_box" && settings.countdown_enabled && (
            <div className="absolute left-1/2 -translate-x-1/2 z-30" style={{ bottom: `${(settings.promo_card_2.offset_y ?? 24) + 150}px` }} data-testid="countdown-above_bottom_box">
              <Countdown />
            </div>
          )}
          <PromoCard
            promo={{ ...CARD2_DEFAULTS, ...settings.promo_card_2 }}
            lang={lang}
            backendUrl={backendUrl}
            anchorStyle={{ position: "absolute", left: "50%", bottom: `${settings.promo_card_2.offset_y ?? 24}px`, transform: "translateX(-50%)", zIndex: 30 }}
          />
        </div>
      )}
    </div>
    <SideButtons />
    {promoAnchor === "corner" && <PromoCard promo={settings.promo_card} lang={lang} backendUrl={backendUrl} />}
    </>
  );
}
